import React from 'react';
import { ApiaApi, makeApiaUrl } from '../../..';
import {
  TApiaLoad,
  TApiaFunction,
  TApiaFilterValue,
  arrayOrArray,
  noNaN,
} from '@apia/util';
import { TResponsiveTableRow, TResponsiveTableCell } from '@apia/table';
import { IFetcher, TModalData } from './types';

function parseResult(result?: TApiaLoad<TApiaFunction> | null): TModalData {
  if (result) {
    const { pageInfo } = result.function.messages.result;
    const { table } = result.function.messages.result;

    if (table) {
      table.row = arrayOrArray(table.row);

      const returnRows: TResponsiveTableRow[] = table.row.map((currentRow) => {
        const returnRow: TResponsiveTableRow = {
          id: currentRow.id,
          cells: arrayOrArray(currentRow.cell).map((currentCell) => {
            const children =
              typeof currentCell === 'string' ? currentCell : currentCell.label;
            const returnCell: TResponsiveTableCell = {
              children,
            };
            return returnCell;
          }),
          className: `${currentRow.classToAdd ?? ''} ${
            currentRow.unselectableTR ? 'locked' : ''
          }`,
        };
        return returnRow;
      });

      return {
        currentPage: Number.parseInt(pageInfo.currentPage, 10),
        pageCount: Number.parseInt(pageInfo.pageCount, 10),
        reachedMax: pageInfo.reachedMax,
        rows: returnRows,
        recordsCount: noNaN(pageInfo.totalRecords),
      };
    }
  }

  return {
    currentPage: 0,
    pageCount: 0,
    reachedMax: true,
    rows: [],
    recordsCount: 0,
  };
}

const defaultFetcher: IFetcher = {
  async filter({ filters, parameters, path, requestConfig }) {
    const filterValues: Record<string | number, TApiaFilterValue | boolean> = {
      selectOnlyOne: false,
    };
    (filters ?? []).forEach((currentFilter) => {
      filterValues[currentFilter.id] = currentFilter.currentValue;
    });
    const result = await ApiaApi.post<TApiaLoad<TApiaFunction>>(
      makeApiaUrl({
        react: true,
        action: 'filter',
        ajaxUrl: `/${path}`,
        isAjax: true,
        timestamp: Date.now(),
        stringifyOptions: requestConfig?.stringifyOptions,
      }),
      {
        ...requestConfig,
        postDataTreatement: 'stringify',
        postData: { ...parameters, ...filterValues },
      },
    );
    return parseResult(result?.data);
  },
  async page({ page, path, requestConfig }) {
    const result = await ApiaApi.post<TApiaLoad<TApiaFunction>>(
      makeApiaUrl({
        react: true,
        action: 'page',
        ajaxUrl: `/${path}`,
        pageNumber: page,
        isAjax: true,
        stringifyOptions: requestConfig?.stringifyOptions,
      }),
      {
        ...requestConfig,
      },
    );
    return parseResult(result?.data);
  },
  async refresh({ path, requestConfig }) {
    const result = await ApiaApi.post<TApiaLoad<TApiaFunction>>(
      makeApiaUrl({
        react: true,
        action: 'refresh',
        ajaxUrl: `/${path}`,
        isAjax: true,
        stringifyOptions: requestConfig?.stringifyOptions,
      }),
      {
        ...requestConfig,
      },
    );
    return parseResult(result?.data);
  },
  async sort({ path, requestConfig, sort }) {
    const result = await ApiaApi.post<TApiaLoad<TApiaFunction>>(
      makeApiaUrl({
        react: true,
        action: 'sort',
        ajaxUrl: `/${path}`,
        isAjax: true,
        stringifyOptions: requestConfig?.stringifyOptions,
      }),
      {
        ...requestConfig,
        postData: {
          orderBy: sort,
        },
        postDataTreatement: 'stringify',
      },
    );
    return parseResult(result?.data);
  },
};

export function useFetcher(fetcher = defaultFetcher) {
  const [isLoading, setIsLoading] = React.useState(0);

  const proxy = React.useMemo(() => {
    return new Proxy(fetcher, {
      get(obj, prop) {
        if (prop in obj) {
          const method = obj[prop as keyof typeof obj];
          return (args: Parameters<typeof method>) => {
            return new Promise((resolve, reject) => {
              setIsLoading((currentIndex) => currentIndex + 1);

              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any
              method(args as any).then((result) => {
                setIsLoading((currentIndex) => currentIndex - 1);

                resolve(result);
              }, reject);
            });
          };
        }

        return undefined;
      },
    });
  }, [fetcher]);

  return { fetcher: proxy, isLoading: isLoading > 0 };
}

export default defaultFetcher;
